import { pipeTtsErrorToServer } from "./ttsClientLog.js";
import type { TtsSettings } from "../../shared/tts.js";
import { getTtsSettingsSignature, resolveTtsSettings } from "../../shared/tts.js";

export interface LineTimestamp {
  start: number;
  end: number;
  line: number;
  fromChar: number;
  toChar: number;
}

export interface TtsChunk {
  index: number;
  text: string;
  lineOffset: number;
  lineCount: number;
  charOffset: number;
  key: string;
}

export interface TtsPreparedChunk {
  chunk: TtsChunk;
  audioUrl: string;
  timestamps: LineTimestamp[];
  timestampsReady: Promise<LineTimestamp[]>;
  audioId: string | null;
}

interface PrepareChunkOptions {
  force?: boolean;
}

interface CoreOptions {
  projectId: string;
  getAccessToken: () => Promise<string>;
  settings: TtsSettings;
}

const TARGET_CHARS = 450;
const HARD_MAX_CHARS = 650;
const SIZE_SPLIT_OVERFLOW = 100;
const FAST_POLL_STEPS_MS = [0, 200, 300, 450, 650, 900, 1200, 1500, 1800, 2200];
const MAX_POLL_ATTEMPTS = 45;

function hashString(text: string): string {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isValidTimestamp(x: any): x is LineTimestamp {
  return (
    x &&
    Number.isFinite(x.start) &&
    Number.isFinite(x.end) &&
    Number.isFinite(x.line) &&
    Number.isFinite(x.fromChar) &&
    Number.isFinite(x.toChar) &&
    x.end > x.start &&
    x.toChar > x.fromChar
  );
}

function parseTimestamps(raw: any): LineTimestamp[] {
  const list = Array.isArray(raw) ? raw : [];
  return list.filter(isValidTimestamp) as LineTimestamp[];
}

export function getTimestampDuration(timestamps: LineTimestamp[]): number {
  let maxEnd = 0;
  for (const ts of timestamps) {
    if (ts.end > maxEnd) maxEnd = ts.end;
  }
  return maxEnd;
}

export function buildTtsChunks(
  content: string,
  fromLine = 1,
  fromChar?: number
): TtsChunk[] {
  const lines = content.split("\n");
  if (lines.length === 0) return [];

  const safeFromLine = Math.max(1, Math.min(fromLine, lines.length));

  const lineStarts: number[] = new Array(lines.length);
  let cursor = 0;
  for (let i = 0; i < lines.length; i++) {
    lineStarts[i] = cursor;
    cursor += lines[i].length + 1;
  }

  let startChar = lineStarts[safeFromLine - 1] ?? 0;
  if (typeof fromChar === "number" && Number.isFinite(fromChar)) {
    startChar = Math.max(0, Math.min(content.length, Math.floor(fromChar)));
  }

  let startLine = safeFromLine;
  for (let i = lineStarts.length - 1; i >= 0; i--) {
    if (startChar >= lineStarts[i]) {
      startLine = i + 1;
      break;
    }
  }

  const segments: Array<{
    line: number;
    charStart: number;
    text: string;
    isBoundary: boolean;
  }> = [];

  for (let absLine = startLine; absLine <= lines.length; absLine++) {
    const lineIdx = absLine - 1;
    const rawLine = lines[lineIdx];
    const lineStart = lineStarts[lineIdx] ?? 0;
    const sliceStart = absLine === startLine ? Math.max(0, startChar - lineStart) : 0;
    const lineText = rawLine.slice(sliceStart);
    const withNewline = absLine < lines.length ? `${lineText}\n` : lineText;

    if (!withNewline) continue;

    segments.push({
      line: absLine,
      charStart: lineStart + sliceStart,
      text: withNewline,
      isBoundary: rawLine.trim() === "" || /^#{1,6}\s/.test(rawLine),
    });
  }

  const chunks: TtsChunk[] = [];
  let current = "";
  let currentStartLine = startLine;
  let currentStartChar = startChar;
  let currentLineCount = 0;

  const pushCurrent = () => {
    if (!current.trim()) {
      current = "";
      currentLineCount = 0;
      return;
    }
    const key = hashString(`${currentStartChar}:${current}`);
    chunks.push({
      index: chunks.length,
      text: current,
      lineOffset: currentStartLine,
      lineCount: Math.max(1, currentLineCount),
      charOffset: currentStartChar,
      key,
    });
    current = "";
    currentLineCount = 0;
  };

  for (const segment of segments) {
    const withNewline = segment.text;
    const isBoundary = segment.isBoundary;

    const wouldExceedHard = current.length > 0 && current.length + withNewline.length > HARD_MAX_CHARS;
    const shouldSplitSoft = current.length >= TARGET_CHARS && isBoundary;
    const shouldSplitSize = current.length >= TARGET_CHARS + SIZE_SPLIT_OVERFLOW;

    if (wouldExceedHard || shouldSplitSoft || shouldSplitSize) {
      pushCurrent();
      currentStartLine = segment.line;
      currentStartChar = segment.charStart;
    }

    if (!current) {
      currentStartLine = segment.line;
      currentStartChar = segment.charStart;
    }

    current += withNewline;
    currentLineCount += 1;

    if (current.length >= HARD_MAX_CHARS) {
      pushCurrent();
    }
  }

  pushCurrent();
  return chunks;
}

export class TtsRenderCore {
  private projectId: string;
  private getAccessToken: () => Promise<string>;
  private settings: TtsSettings;
  private signature: string;
  private cache = new Map<string, Promise<TtsPreparedChunk>>();

  constructor(options: CoreOptions) {
    this.projectId = options.projectId;
    this.getAccessToken = options.getAccessToken;
    this.settings = resolveTtsSettings(options.settings);
    this.signature = getTtsSettingsSignature(this.settings);
  }

  updateSettings(next: TtsSettings) {
    const resolved = resolveTtsSettings(next);
    const nextSig = getTtsSettingsSignature(resolved);
    this.settings = resolved;
    if (nextSig !== this.signature) {
      this.signature = nextSig;
      this.cache.clear();
    }
  }

  clear() {
    this.cache.clear();
  }

  invalidateChunk(chunk: TtsChunk) {
    this.cache.delete(this.cacheKey(chunk));
  }

  private cacheKey(chunk: TtsChunk): string {
    return `${this.signature}:${chunk.key}`;
  }

  prefetchChunk(chunk: TtsChunk): void {
    this.prepareChunk(chunk).catch(() => {
      // Best effort warmup only.
    });
  }

  prepareChunk(chunk: TtsChunk, options: PrepareChunkOptions = {}): Promise<TtsPreparedChunk> {
    const key = this.cacheKey(chunk);
    if (options.force) {
      this.cache.delete(key);
    } else {
      const existing = this.cache.get(key);
      if (existing) return existing;
    }

    const run = this.doPrepareChunk(chunk, options).catch((err) => {
      this.cache.delete(key);
      void pipeTtsErrorToServer({
        scope: "tts_core.prepare_chunk",
        message: "Failed to prepare TTS chunk",
        projectId: this.projectId,
        error: err,
        meta: {
          chunkIndex: chunk.index,
          lineOffset: chunk.lineOffset,
          lineCount: chunk.lineCount,
          force: Boolean(options.force),
        },
      });
      throw err;
    });

    this.cache.set(key, run);
    return run;
  }

  private async doPrepareChunk(chunk: TtsChunk, options: PrepareChunkOptions): Promise<TtsPreparedChunk> {
    const token = await this.getAccessToken();
    const prepareStartedAt = Date.now();

    const body: Record<string, any> = {
      text: chunk.text,
      settings: this.settings,
      lineOffset: chunk.lineOffset,
      force: Boolean(options.force),
    };

    const res = await fetch(`/api/tts/${this.projectId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "TTS failed" }));
      const errorMessage =
        typeof err?.error === "string" && err.error
          ? err.error
          : "TTS failed";
      throw new Error(`TTS failed (${res.status}): ${errorMessage}`);
    }

    const data = await res.json();
    const timestamps = parseTimestamps(data.timestamps);
    const audioId = typeof data.audioId === "string" ? data.audioId : "";
    const audioPath =
      typeof data.audioPath === "string" && data.audioPath
        ? data.audioPath
        : `/api/tts/${this.projectId}/audio/${audioId}`;
    if (!audioId) {
      throw new Error("TTS prepare returned no audio id");
    }

    const audioVersion =
      typeof data.audioVersion === "number" && Number.isFinite(data.audioVersion)
        ? data.audioVersion
        : Date.now();
    const query = new URLSearchParams({
      token,
      v: String(audioVersion),
    });
    const audioUrl = `${audioPath}?${query.toString()}`;
    const hasImmediateTimestamps = Array.isArray(data.timestamps);
    const timestampsReady = hasImmediateTimestamps
      ? Promise.resolve(timestamps)
      : this.pollTimestamps(audioId, token, chunk.lineOffset);

    void pipeTtsErrorToServer({
      scope: data.cached ? "tts_core.prepare_chunk.cached" : "tts_core.prepare_chunk.generated",
      message: data.cached
        ? "Prepared chunk from disk cache"
        : "Prepared chunk from durable audio endpoint",
      level: "info",
      projectId: this.projectId,
      token,
      meta: {
        chunkIndex: chunk.index,
        lineOffset: chunk.lineOffset,
        lineCount: chunk.lineCount,
        chars: chunk.text.length,
        audioId,
        cached: Boolean(data.cached),
        hasTimestamps: timestamps.length > 0,
        timestampCount: timestamps.length,
        elapsedMs: Date.now() - prepareStartedAt,
      },
    });

    return {
      chunk,
      audioUrl,
      timestamps,
      timestampsReady,
      audioId,
    };
  }

  private async pollTimestamps(
    audioId: string,
    token: string,
    lineOffset: number
  ): Promise<LineTimestamp[]> {
    let networkError: unknown = null;
    let nonOkCount = 0;
    const statusSamples: number[] = [];
    const startedAt = Date.now();

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      const delay = FAST_POLL_STEPS_MS[Math.min(attempt, FAST_POLL_STEPS_MS.length - 1)] ?? 2200;
      if (delay > 0) await sleep(delay);

      try {
        const res = await fetch(
          `/api/tts/${this.projectId}/timestamps/${audioId}?lineOffset=${encodeURIComponent(String(lineOffset))}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          nonOkCount += 1;
          if (statusSamples.length < 6) statusSamples.push(res.status);
          continue;
        }

        const data = await res.json();
        if (data.ready) {
          const parsed = parseTimestamps(data.timestamps);
          void pipeTtsErrorToServer({
            scope: "tts_core.srt_ready",
            message: "STT/SRT timestamps ready",
            level: "info",
            projectId: this.projectId,
            token,
            meta: {
              audioId,
              attempts: attempt + 1,
              timestampCount: parsed.length,
              elapsedMs: Date.now() - startedAt,
            },
          });
          return parsed;
        }
      } catch (err) {
        if (!networkError) networkError = err;
        // keep retrying
      }
    }

    void pipeTtsErrorToServer({
      scope: "tts_core.srt_timeout",
      message: "STT/SRT polling exhausted before timestamps became available",
      level: networkError ? "error" : "warn",
      projectId: this.projectId,
      token,
      error: networkError,
      meta: {
        audioId,
        attempts: MAX_POLL_ATTEMPTS,
        nonOkCount,
        statusSamples,
        elapsedMs: Date.now() - startedAt,
      },
    });

    return [];
  }
}
